import { CircularProgress, LinearProgress, Link } from "@mui/material";
import DetailsPanel from "components/Elements/DetailsPanel";
import SearchField from "components/Elements/SearchField/SearchField";
import { debounce, getLocalStorageData, getFormattedLink } from "helpers/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { showError } from "../../../redux/common/actions";
import AddNewNoticeBoardDetail from "./AddNoticeBoard";
import Header from "./Header";
import { makeStyles } from "tss-react/mui";
import NoticeBoardListTable from "./NoticeBoardListTable";
import { defaultPageSize } from "helpers/utils";
import ColourConstants from "helpers/colourConstants";
import DeleteDialog from "components/Elements/DeleteDialog";
import {
	getCountOfNoticeBoardsList,
	getNoticeBoardsList,
	postNewNoticeBoards,
	postNewNoticeBoardsFile,
} from "services/noticeboards/noticeBoardsList";
import {
	NoticeBoardsTableColumns,
	NoticeBoardsTableHeader,
} from "constants/NoticeBoards";
import TabTitle from "components/Elements/TabTitle";
import HistoryBar from "components/Modules/HistorySidebar/HistoryBar";
import { noticeBoardsPage } from "services/History/models";
import { HistoryCaptions } from "helpers/constants";
import {
	makeGetNextPageParam,
	removeRowFromInfiniteCache,
	flattenPages,
} from "helpers/infiniteQuery";

const useStyles = makeStyles()((theme) => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
		right: 0,
	},
}));

const NOTICEBOARDS_LIST_KEY = ["noticeboards", "list"];
const NOTICEBOARDS_COUNT_KEY = ["noticeboards", "count"];

const formattedData = (data) => {
	return data.map((x) => ({
		...x,
		name: (
			<Link
				style={{
					color: ColourConstants.activeLink,
					cursor: "pointer",
					textDecoration: "underline",
				}}
				href={
					x?.documentURL !== null ? x?.documentURL : getFormattedLink(x?.link)
				}
				download={x?.name}
				target="_blank"
				rel="noopener"
			>
				{x.name}
			</Link>
		),
	}));
};

function NoticeBoardsList() {
	const dispatch = useDispatch();
	const { classes } = useStyles();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const {
		customCaptions,
		siteAppID,
		siteID,
		application,
		position: { noticeboardAccess },
	} = getLocalStorageData("me");

	const [currentTableSort, setCurrentTableSort] = useState(["", "asc"]);
	const [openAddNoticeBoard, setOpenAddNoticeBoard] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isSearching, setSearching] = useState(false);
	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [noticeToEdit, setNoticeTOEdit] = useState({
		openEdit: false,
		notice: {},
	});

	const [dataForFetchingNoticeBoard, setDataForFetchingNoticeBoard] = useState({
		pageNumber: 1,
		pageSize: defaultPageSize(),
		search: "",
		sortField: "",
		sort: "",
	});

	const [searchText, setSearchText] = useState("");
	const [pageSize] = useState(() => defaultPageSize());

	const queryInputs = useMemo(
		() => ({
			siteAppId: siteAppID,
			sortField: currentTableSort[0],
			sortOrder: currentTableSort[1],
			search: searchText,
		}),
		[siteAppID, currentTableSort, searchText]
	);

	const noticeboardsQueryKey = useMemo(
		() => [...NOTICEBOARDS_LIST_KEY, { ...queryInputs, pageSize }],
		[queryInputs, pageSize]
	);

	const noticeboardsQuery = useInfiniteQuery({
		queryKey: noticeboardsQueryKey,
		enabled: Boolean(siteAppID),
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const response = await getNoticeBoardsList({
				...queryInputs,
				pageNumber: pageParam,
				pageSize,
			});
			if (!response?.status) {
				throw new Error(
					response?.data?.detail ||
						t("message.failedToFetchEntityList", {
							entity: customCaptions?.tutorial,
						})
				);
			}
			return { rows: Array.isArray(response.data) ? response.data : [], pageParam };
		},
		getNextPageParam: makeGetNextPageParam(pageSize),
	});

	useEffect(() => {
		if (noticeboardsQuery.isError) {
			dispatch(
				showError(
					noticeboardsQuery.error?.message ||
						t("message.failedToFetchEntityList", {
							entity: customCaptions?.tutorial,
						})
				)
			);
		}
	}, [noticeboardsQuery.isError, noticeboardsQuery.error, dispatch, customCaptions?.tutorial, t]);

	const countQuery = useQuery({
		queryKey: [...NOTICEBOARDS_COUNT_KEY, { siteAppId: siteAppID, search: searchText }],
		enabled: Boolean(siteAppID),
		queryFn: async () => {
			const response = await getCountOfNoticeBoardsList({
				siteAppId: siteAppID,
				search: searchText,
			});
			return response?.status ? response.data : 0;
		},
	});

	const allData = useMemo(() => flattenPages(noticeboardsQuery.data), [noticeboardsQuery.data]);
	const countOFNoticeBoard = countQuery.data ?? 0;

	const refreshNoticeboards = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: NOTICEBOARDS_LIST_KEY });
		return queryClient.invalidateQueries({ queryKey: NOTICEBOARDS_COUNT_KEY });
	}, [queryClient]);

	useEffect(() => {
		setLoading(false);
		document.body.style.overflowX = "hidden";
		document.body.style.maxWidth = "100%";
	}, [siteID]);

	const handleSearch = useCallback(
		debounce((value) => {
			setSearchText(value);
		}, 500),
		[]
	);

	const addNewFeedback = async (payload) => {
		let response, callApi;
		if (payload.file) {
			response = await postNewNoticeBoardsFile({ filename: payload.file.name });
			if (response.status) {
				try {
					await fetch(response.data.url, {
						method: "PUT",
						body: payload.file,
					});
					callApi = true;
				} catch (error) {
					callApi = false;
				}
			} else {
				callApi = false;
			}
		} else {
			callApi = true;
		}

		if (callApi) {
			return await postNewNoticeBoards({
				siteAppID: siteAppID,
				name: payload.name,
				documentKey: response?.data?.key ?? null,
				link: payload?.link !== "" ? payload.link : null,
				description: payload.description,
				expiryDate: payload.expiryDate,
				siteDepartments: payload.siteDepartments,
				autoDelete: payload.autoDelete,
			});
		} else {
			return { status: false, data: { detail: t("message.fileUploadFailed") } };
		}
	};

	const handleDeleteDialogOpen = (id) => {
		setDeleteID(id);
		setOpenDeleteDialog(true);
	};

	const handleEditDialogOpen = (notice) => {
		const localNoticeToEdit = allData.find((x) => x.id === notice.id);
		setNoticeTOEdit({ openEdit: true, notice: localNoticeToEdit });
	};

	const handleRemoveData = (id) => {
		removeRowFromInfiniteCache(queryClient, noticeboardsQueryKey, (row) => row.id === id);
		queryClient.invalidateQueries({ queryKey: NOTICEBOARDS_COUNT_KEY });
	};

	const { isHistoryDrawerOpen } = useSelector((state) => state.commonData);
	if (loading) return <CircularProgress />;

	const tableIsRefreshing =
		(noticeboardsQuery.isFetching && !noticeboardsQuery.isFetchingNextPage) ||
		countQuery.isFetching;

	return (
		<>
			<TabTitle
				title={`${customCaptions.tutorialPlural} | ${application.name}`}
			/>
			{(isSearching || tableIsRefreshing) && <LinearProgress className={classes.loading} />}
			<HistoryBar
				id={siteAppID}
				showhistorybar={isHistoryDrawerOpen}
				dispatch={dispatch}
				fetchdata={(id, pageNumber, pageSize) =>
					noticeBoardsPage(id, pageNumber, pageSize)
				}
				origin={HistoryCaptions.noticeBoard}
			/>
			<DeleteDialog
				entityName={customCaptions?.tutorial}
				open={openDeleteDialog}
				closeHandler={() => setOpenDeleteDialog(false)}
				deleteID={deleteID}
				deleteEndpoint={"/api/Noticeboards"}
				handleRemoveData={handleRemoveData}
			/>
			<AddNewNoticeBoardDetail
				open={openAddNoticeBoard}
				closeHandler={() => setOpenAddNoticeBoard(false)}
				siteAppId={siteAppID}
				title={t("title.addEntity", { entity: customCaptions?.tutorial })}
				createProcessHandler={addNewFeedback}
				customCaptions={customCaptions}
				setSearchQuery={() => {}}
				fetchData={refreshNoticeboards}
				setDataForFetchingNoticeBoard={setDataForFetchingNoticeBoard}
				siteID={siteID}
			/>
			<AddNewNoticeBoardDetail
				open={noticeToEdit.openEdit}
				closeHandler={() =>
					setNoticeTOEdit({
						openEdit: false,
						notice: {},
					})
				}
				siteAppId={siteAppID}
				title={t("title.editEntity", { entity: customCaptions?.tutorial })}
				createProcessHandler={addNewFeedback}
				customCaptions={customCaptions}
				setSearchQuery={() => {}}
				fetchData={refreshNoticeboards}
				setDataForFetchingNoticeBoard={setDataForFetchingNoticeBoard}
				siteID={siteID}
				data={noticeToEdit.notice}
				isEdit={true}
			/>

			<div className="container">
				<Header
					setOpenAddNoticeBoard={setOpenAddNoticeBoard}
					dataLength={countOFNoticeBoard}
					noticeBoardCC={customCaptions?.tutorialPlural}
				/>
				<div
					className="detailsContainer"
					style={{ alignItems: "center", marginTop: "-15px" }}
				>
					<DetailsPanel
						showHeader={false}
						description={t("message.manageDocumentsAvailable")}
					/>
					<SearchField
						searchQuery={dataForFetchingNoticeBoard?.search}
						setSearchQuery={(e) => {
							e.persist();
							setDataForFetchingNoticeBoard((prev) => ({
								...prev,
								search: e.target.value,
							}));
							handleSearch(e.target.value);
						}}
					/>
				</div>
				<div style={{ height: 30 }}></div>
				<div className="dynamic-width-table">
					<NoticeBoardListTable
						data={allData}
						headers={NoticeBoardsTableHeader(customCaptions)}
						columns={NoticeBoardsTableColumns()}
						handleSort={(sortField, sort) => {
							setCurrentTableSort([sortField, sort]);
						}}
						formattedData={formattedData}
						handleDeleteDialogOpen={handleDeleteDialogOpen}
						handleEditDialogOpen={handleEditDialogOpen}
						currentTableSort={currentTableSort}
						setCurrentTableSort={setCurrentTableSort}
						isReadOnly={noticeboardAccess === "R"}
						hasNextPage={noticeboardsQuery.hasNextPage}
						isFetchingNextPage={noticeboardsQuery.isFetchingNextPage}
						fetchNextPage={noticeboardsQuery.fetchNextPage}
						isFetchNextPageError={noticeboardsQuery.isFetchNextPageError}
					/>
				</div>
			</div>
		</>
	);
}

export default NoticeBoardsList;
